import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Observable} from 'rxjs';
import {filter, map, switchMap, take, tap} from 'rxjs/operators';

import {FeatureFlags, FeatureFlagService, SurvicateService} from '@core';
import {ModalService, TimeService} from '@shared';

import {
    ActiveEmployeeService, BreakpointObserverService, BreakpointValue,
    EmployeeView,
    NavigationService,
    ProblemInstanceService, RouteKeyNumbers,
    RoutePlanningService,
    RoutesService,
    TravelTimeTransportService,
    Visit,
    VisitList
} from 'planner-shared';

import {RoutePlanningEmployeesComponent} from './employees/route-planning-employees.component';
import {RoutePlanningExportComponent} from './export/route-planning-export.component';
import {RoutePlanningUnallocatedTasksComponent} from './tasks/unallocated/route-planning-unallocated-tasks.component';
import {UnallocatedTaskService} from './tasks/unallocated/services/unallocated-task.service';
import {
    TravelTimeImprovementSuggestionsComponent
} from './travel-time-improvement-suggestions/travel-time-improvement-suggestions.component';
import {RouteKeyNumberWeights} from '../shared/models/route-key-number-weights.model';
import {TravelTimeTransport} from '../shared/models/travel-time-transport';
import {RouteDelayService} from "./tasks/services/route-delay.service";

@UntilDestroy()
@Component({
    selector: 'app-route-planning',
    templateUrl: './route-planning.component.html',
    styleUrls: ['./route-planning.component.scss'],
    providers: [ActiveEmployeeService],
    standalone: false
})
export class RoutePlanningComponent implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly unallocatedTaskService = inject(UnallocatedTaskService);
    private readonly routePlanningService = inject(RoutePlanningService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly survicateService = inject(SurvicateService);
    private readonly navigationService = inject(NavigationService);
    private readonly routesService = inject(RoutesService);
    private readonly modalService = inject(ModalService);
    private readonly timeService = inject(TimeService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly breakPointObserverService = inject(BreakpointObserverService);
    private readonly routeDelayService = inject(RouteDelayService);
    readonly travelTimeTransportService = inject(TravelTimeTransportService);

    public selectedEmployee: EmployeeView;
    public routes: VisitList;
    public employeeKeyNumbers: RouteKeyNumbers;
    public selectedEmployeeTasks: Visit[];
    public changingEmployeeTasks: boolean;
    public updating: boolean;
    public manualChanges: boolean;
    public manualChangesEnabled: boolean;
    public travelTimeImprovementsCount$: Observable<number>;
    public routeLength: number;
    public routeKeyNumberWeights: RouteKeyNumberWeights;
    public rosterInInstance: boolean;
    public transportTravelTime: TravelTimeTransport;
    public travelTimeTooltip: string;
    public viewMinimized = false;

    public minimizeViewFeatureEnabled: Observable<boolean>;
    public breakpointValue: BreakpointValue;

    @ViewChild(RoutePlanningEmployeesComponent)
    private readonly routePlanningEmployeesComponent: RoutePlanningEmployeesComponent;
    @ViewChild(RoutePlanningUnallocatedTasksComponent)
    private readonly routePlanningUnallocatedTasksComponent: RoutePlanningUnallocatedTasksComponent;

    ngOnInit(): void {
        this.route.data.subscribe((data: { routes: VisitList }) => {
            setTimeout(() => {
                this.navigationService.nextPage();
                this.loadRoutesInfo(data.routes, true);
            });
        });
        this.unallocatedTaskService.clearTaskFeasibility();
        this.survicateService.tryShowSurveyAuto()
            .pipe(
                take(1),
                untilDestroyed(this)
            )
            .subscribe();

        this.featureFlagService.isEnabled(FeatureFlags.ManualRoutes).subscribe(enabled => {
            this.manualChangesEnabled = enabled;
        });

        this.routePlanningService
            .getEmployeeTaskUpdateStateChange()
            .pipe(untilDestroyed(this))
            .subscribe(updating => {
                this.navigationService.showResetButtonOnly(this.manualChanges);
                this.changingEmployeeTasks = updating;
                this.manualChanges = true;
                this.navigationService.activateSaveAndResetButtons(!updating);
            });

        this.navigationService.showResetButtonOnly(false);

        this.navigationService
            .resetButtonClick()
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.reset();
                this.navigationService.activateSaveAndResetButtons(false);
            });
        this.routeKeyNumberWeights = <RouteKeyNumberWeights>{
            accurateArrivals: [],
            travelTime: [],
            taskTime: [],
            totalTaskTime: []
        };
        this.transportTravelTime = this.travelTimeTransportService.resetTransportTravelTime();
        this.routesService.getRoutes().subscribe(routeResult => {
            this.travelTimeTransportService.mapToTravelTimeTransport(routeResult.routes.routeKeyNumbers)
                .pipe(untilDestroyed(this))
                .subscribe(transportTravelTime => {
                    this.transportTravelTime = transportTravelTime;
                    this.travelTimeTooltip
                        = this.travelTimeTransportService.formatTransportTooltip(this.transportTravelTime);
                });
        });
        this.problemInstanceService.rosterDepartmentIsPartOfInstance()
            .pipe(untilDestroyed(this))
            .subscribe(result => this.rosterInInstance = result.valueOf());

        this.breakPointObserverService
            .getCurrentBreakpoint()
            .pipe(untilDestroyed(this))
            .subscribe(value => {
                this.breakpointValue = value;
            });

        this.minimizeViewFeatureEnabled = this.featureFlagService.isEnabled(FeatureFlags.MinimizeViewLastStep);
    }

    ngOnDestroy(): void {
        this.navigationService.showResetButtonOnly(false);
        this.navigationService.activateSaveAndResetButtons(false);
    }

    public employeeSelected(employee: EmployeeView): void {
        if (employee !== null && employee.employeeId) {
            this.selectedEmployee = employee;
            this.employeeKeyNumbers = this.routes.routeKeyNumbers.find(
                route => route.workHourId === employee.workHourId
            );
            this.selectedEmployeeTasks = this.routes.tasks.filter(task => task.workHourId === employee.workHourId);
        }
    }

    public openExportModal(): void {
        this.modalService.openCustomModal(RoutePlanningExportComponent, () => {
        }, null, null, true);
    }

    public reset(): void {
        this.updating = true;
        this.routesService
            .deleteManualChanges()
            .pipe(switchMap(() => this.loadRoutes()))
            .subscribe(() => {
                this.resetState();
                this.routePlanningUnallocatedTasksComponent.loadTasks(this.routes.nonAllocatedTasksInfo);
            });
    }

    public loadRoutesInfo(routes: VisitList, reloadEmployees?: boolean): void {
        this.routes = routes;
        if (routes.totalKeyNumbers?.mrpInvolved) {
            this.manualChanges = true;
            this.navigationService.showResetButtonOnly(true);
            this.navigationService.activateSaveAndResetButtons(true);
        }
        this.featureFlagService.isEnabled(FeatureFlags.HighlightDelays)
            .pipe(
                take(1),
                untilDestroyed(this),
                filter(isEnabled => isEnabled),
                tap(() => this.routeDelayService.evaluateRouteDelays(routes))
            )
            .subscribe();

        this.routeLength = routes.tasks.length;
        if (reloadEmployees) {
            this.routePlanningEmployeesComponent.init(routes.tasks, routes.routeKeyNumbers);
        }

        if (this.selectedEmployee) {
            this.employeeKeyNumbers = this.routes.routeKeyNumbers.find(
                route => route.workHourId === this.selectedEmployee.workHourId
            );
            this.selectedEmployeeTasks = this.routes.tasks.filter(
                task => task.workHourId === this.selectedEmployee.workHourId
            );
            this.loadWeights();
        }
    }

    public openTravelTimeImprovementsModal(): void {
        this.modalService.openCustomModal(
            TravelTimeImprovementSuggestionsComponent,
            (reloadRoutes: boolean) => {
                if (reloadRoutes) {
                    this.loadRoutesAfterSuggestionsHandled();
                }
            },
            (reloadRoutes: boolean) => {
                if (reloadRoutes) {
                    this.loadRoutesAfterSuggestionsHandled();
                }
            },
            null,
            true
        );
    }


    public updateViewState() {
        this.viewMinimized = !this.viewMinimized;
    }

    private loadRoutes(): Observable<void> {
        return this.routesService.getRoutes().pipe(
            tap(response => {
                if (response.routes) {
                    this.loadRoutesInfo(response.routes, true);
                    this.routePlanningEmployeesComponent.applyRevertedChanges(this.routes);
                }
            }),
            map(() => {
            })
        );
    }

    private loadRoutesAfterSuggestionsHandled(): void {
        this.updating = true;
        this.loadRoutes().subscribe(() => (this.updating = false));
    }

    private resetState(): void {
        this.updating = false;
        this.manualChanges = false;
        this.navigationService.showResetButtonOnly(false);
    }

    private loadWeights(): void {
        this.routeKeyNumberWeights.allocatedVisits = this.routeLength;
        this.routeKeyNumberWeights.accurateArrivals[0] = this.timeService
            .extractHours(this.routes.totalKeyNumbers.totalTimeBreakingTimeWindowsInSeconds);
        this.routeKeyNumberWeights.accurateArrivals[1] = this.timeService
            .extractMinutes(this.routes.totalKeyNumbers.totalTimeBreakingTimeWindowsInSeconds);
        this.routeKeyNumberWeights.travelTime[0] = this.timeService
            .extractHours(this.routes.totalKeyNumbers.totalTravelTimeSeconds);
        this.routeKeyNumberWeights.travelTime[1] = this.timeService
            .extractMinutes(this.routes.totalKeyNumbers.totalTravelTimeSeconds);
        this.routeKeyNumberWeights.visitContinuity
            = this.routes.totalKeyNumbers.totalNumberOfPatientsVisitedByEmployeesNotInPrimaryGroup;
        this.routeKeyNumberWeights.correctCompetence
            = this.routes.totalKeyNumbers.numberOfTasksWithOverqualifiedWork;
        this.routeKeyNumberWeights.overMaxEmployeeCount
            = this.routes.totalKeyNumbers.numberOfPatientsVisitedByOverMaxEmployees;
        this.routeKeyNumberWeights.differentRosterTasks
            = this.routes.totalKeyNumbers.numberOfTasksWithDifferentRosterDepartment;
        this.routeKeyNumberWeights.totalTaskTime[0] = this.timeService
            .extractHours(this.routes.totalKeyNumbers.totalPatientTime);
        this.routeKeyNumberWeights.totalTaskTime[1] = this.timeService
            .extractMinutes(this.routes.totalKeyNumbers.totalPatientTime);
    }
}
